/**
 * Cloudflare Turnstile — the bot check on the sign-in screen.
 *
 * Supabase verifies the token server-side (Auth → Attack Protection →
 * CAPTCHA). Once that switch is on, EVERY sign-up, password sign-in,
 * magic-link and password-reset request must carry a fresh token or
 * Supabase refuses it. Confirming an emailed code and setting a new
 * password are not gated.
 *
 * The check runs ON SUBMIT, not on page load (execution: 'execute'), and
 * stays invisible unless Cloudflare actually wants a click
 * (appearance: 'interaction-only'). Earlier builds ran it on every page
 * load, re-ran it after every attempt, and re-ran it whenever the token
 * aged out (5 min) — on a phone that looked like the check firing over and
 * over. Now a coach who passes silently never sees it at all.
 *
 *  - The site key is baked into the build (VITE_TURNSTILE_SITE_KEY). With no
 *    key nothing renders and no token is sent — the pre-captcha behaviour.
 *  - Tokens are single use: `getToken()` earns one, the caller spends it,
 *    and the widget is reset so the next attempt earns a new one.
 *
 * NATIVE APPS. Turnstile checks the hostname of the page it runs on, and
 * the native builds have none it will accept (iOS serves the app from
 * capacitor://localhost, Android from https://localhost). So in a native
 * build the widget runs in a small iframe of readybreak.app/turnstile-bridge/
 * — an allowed hostname — and the token comes back by postMessage. The
 * bridge only ever posts to the origin the app named, and only if that is one
 * of the three native origins; the app only accepts messages from
 * readybreak.app and from its own iframe.
 */
import { Capacitor } from '@capacitor/core';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export const TURNSTILE_SITE_KEY = (import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined)?.trim() || '';

/** Native builds run the check through the readybreak.app bridge iframe. */
const NATIVE = Capacitor.isNativePlatform();
const BRIDGE_ORIGIN = 'https://readybreak.app';
const BRIDGE_URL = `${BRIDGE_ORIGIN}/turnstile-bridge/`;

/** Turnstile can only render on an http(s) page — or, natively, through the bridge. */
export const TURNSTILE_SUPPORTED = NATIVE || (typeof location !== 'undefined' && /^https?:$/.test(location.protocol));

/** True when this build should require a captcha token before any gated auth call. */
export const CAPTCHA_ON = !!TURNSTILE_SITE_KEY && TURNSTILE_SUPPORTED;

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  execute: (id: string) => void;
  reset: (id: string) => void;
  remove: (id: string) => void;
};
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile failed to load')));
    s.onerror = () => {
      scriptPromise = null; // allow a retry on the next attempt
      reject(new Error('Could not load the security check. Check your connection and try again.'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export type TurnstileHandle = {
  /** Resolves with a fresh single-use token (null when captcha is off). */
  getToken: () => Promise<string | null>;
};

type Pending = { resolve: (t: string) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> };

/** How long to wait for a coach to answer a visible challenge. */
const CHALLENGE_TIMEOUT_MS = 120_000;

const WebTurnstile = forwardRef<TurnstileHandle, object>(function WebTurnstile(_props, ref) {
  const box = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const ready = useRef<Promise<void> | null>(null);
  const pending = useRef<Pending | null>(null);
  const [shown, setShown] = useState(false);

  const settle = (fn: (p: Pending) => void) => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    clearTimeout(p.timer);
    setShown(false);
    fn(p);
  };

  const mount = () => {
    if (ready.current) return ready.current;
    ready.current = loadTurnstile().then((ts) => {
      if (!box.current) throw new Error('The security check is not on screen.');
      widgetId.current = ts.render(box.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'dark',
        size: 'flexible',
        action: 'auth',
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (t: string) => settle((p) => p.resolve(t)),
        'before-interactive-callback': () => setShown(true),
        'after-interactive-callback': () => setShown(false),
        'expired-callback': () => undefined, // never cached: a token is spent right after it arrives
        'timeout-callback': () => settle((p) => p.reject(new Error('The security check timed out. Try again.'))),
        'error-callback': (code: string) => {
          settle((p) => p.reject(new Error(`The security check didn't pass (${code}). Try again, or try Wi-Fi / mobile data instead.`)));
          return true; // handled — don't let Turnstile loop on its own
        },
      });
    });
    ready.current.catch(() => {
      ready.current = null; // let the next attempt try to load it again
    });
    return ready.current;
  };

  useImperativeHandle(ref, () => ({
    getToken: async () => {
      if (!CAPTCHA_ON) return null;
      await mount();
      const ts = window.turnstile!;
      const id = widgetId.current!;
      // A previous run leaves the widget "used"; reset clears it for a fresh run.
      ts.reset(id);
      settle((p) => p.reject(new Error('superseded')));
      return new Promise<string>((resolve, reject) => {
        const timer = setTimeout(() => settle((p) => p.reject(new Error('The security check timed out. Try again.'))), CHALLENGE_TIMEOUT_MS);
        pending.current = { resolve, reject, timer };
        ts.execute(id);
      });
    },
  }));

  useEffect(() => {
    // Load the script early (no challenge runs until submit) so the first
    // tap isn't slowed by the download.
    if (CAPTCHA_ON) void mount().catch(() => undefined);
    return () => {
      settle((p) => p.reject(new Error('closed')));
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
      ready.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!CAPTCHA_ON) return null;
  return (
    <div style={{ margin: shown ? '4px 0 10px' : 0 }}>
      {shown && <p className="muted small" style={{ margin: '0 0 6px' }}>Quick security check — tap the box to continue.</p>}
      <div ref={box} />
    </div>
  );
});

type BridgeMsg = { rbts: 1; type: 'ready' | 'token' | 'show' | 'hide' | 'error'; token?: string; code?: string };

/** How long to wait for the bridge page and Turnstile's script to load. */
const BRIDGE_LOAD_MS = 20_000;

/** Native builds: the same widget, hosted on readybreak.app inside an iframe. */
const NativeTurnstile = forwardRef<TurnstileHandle, object>(function NativeTurnstile(_props, ref) {
  const frame = useRef<HTMLIFrameElement>(null);
  const pending = useRef<Pending | null>(null);
  const ready = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
  const [shown, setShown] = useState(false);

  if (!ready.current) {
    let resolve!: () => void;
    const promise = new Promise<void>((r) => (resolve = r));
    ready.current = { promise, resolve };
  }

  const settle = (fn: (p: Pending) => void) => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    clearTimeout(p.timer);
    setShown(false);
    fn(p);
  };

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== BRIDGE_ORIGIN || e.source !== frame.current?.contentWindow) return;
      const m = e.data as BridgeMsg;
      if (!m || m.rbts !== 1) return;
      if (m.type === 'ready') ready.current!.resolve();
      else if (m.type === 'show') setShown(true);
      else if (m.type === 'hide') setShown(false);
      else if (m.type === 'token' && m.token) settle((p) => p.resolve(m.token!));
      else if (m.type === 'error')
        settle((p) =>
          p.reject(new Error(m.code === 'timeout' ? 'The security check timed out. Try again.' : `The security check didn't pass (${m.code}). Try again, or try Wi-Fi / mobile data instead.`)),
        );
    };
    window.addEventListener('message', onMsg);
    return () => {
      window.removeEventListener('message', onMsg);
      settle((p) => p.reject(new Error('closed')));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    getToken: async () => {
      if (!CAPTCHA_ON) return null;
      let loadTimer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        ready.current!.promise,
        new Promise<never>((_, reject) => {
          loadTimer = setTimeout(() => reject(new Error('Could not load the security check. Check your connection and try again.')), BRIDGE_LOAD_MS);
        }),
      ]).finally(() => clearTimeout(loadTimer));
      settle((p) => p.reject(new Error('superseded')));
      return new Promise<string>((resolve, reject) => {
        const timer = setTimeout(() => settle((p) => p.reject(new Error('The security check timed out. Try again.'))), CHALLENGE_TIMEOUT_MS);
        pending.current = { resolve, reject, timer };
        frame.current?.contentWindow?.postMessage({ rbts: 1, type: 'run' }, BRIDGE_ORIGIN);
      });
    },
  }));

  if (!CAPTCHA_ON) return null;
  const src = `${BRIDGE_URL}?k=${encodeURIComponent(TURNSTILE_SITE_KEY)}&o=${encodeURIComponent(location.origin)}`;
  return (
    <div style={{ margin: shown ? '4px 0 10px' : 0 }}>
      {shown && <p className="muted small" style={{ margin: '0 0 6px' }}>Quick security check — tap the box to continue.</p>}
      <iframe
        ref={frame}
        src={src}
        title="Security check"
        style={{ border: 0, width: 1, minWidth: '100%', height: shown ? 72 : 0, display: 'block', background: 'transparent' }}
      />
    </div>
  );
});

export const Turnstile = NATIVE ? NativeTurnstile : WebTurnstile;
